import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { QueueScene } from "./QueueScene";
import { scrollToSection } from "../lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const LINES = [
    <>We Drive the Traffic.</>,
    <>You Keep the <span className="highlighter px-1">Queue Full.</span></>,
];

export const Hero = ({ onOpenModal }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yHead = useTransform(scrollYProgress, [0, 1], [0, -50]);
    const yScene = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const sceneOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.25]);

    return (
        <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pb-20 pt-32 lg:pt-40">
            <motion.svg
                className="float-slow pointer-events-none absolute right-[8%] top-28 hidden lg:block"
                style={{ "--r": "10deg" }}
                width="72" height="72" viewBox="0 0 72 72" fill="none"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 0.8 }}
            >
                <path d="M36 6v60M6 36h60M14 14l44 44M58 14L14 58" stroke="#FACC15" strokeWidth="5" strokeLinecap="round" />
            </motion.svg>
            <motion.svg
                className="float-slow pointer-events-none absolute left-[7%] top-[610px] hidden lg:block"
                style={{ "--r": "-8deg", animationDelay: "1.2s" }}
                width="90" height="60" viewBox="0 0 90 60" fill="none"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 0.8 }}
            >
                <path d="M4 50C24 14 56 8 84 22M84 22l-14-6M84 22l-4 15" stroke="#1D4ED8" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
            </motion.svg>

            <div className="mx-auto max-w-6xl px-5 lg:px-8">
                <motion.div style={{ y: yHead }}>
                    <motion.p
                        data-testid="hero-eyebrow"
                        className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-inkblue"
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
                    >
                        24queue — Growth Systems, On Paper &amp; In Production
                    </motion.p>

                    <h1 data-testid="hero-headline" className="mt-6 font-display text-4xl font-extrabold leading-[1.07] tracking-tight text-ink sm:text-5xl lg:text-6xl">
                        {LINES.map((line, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span
                                    className="block"
                                    initial={{ y: "112%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 0.9, delay: 0.3 + i * 0.14, ease: EASE }}
                                >
                                    {line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        data-testid="hero-subline"
                        className="mt-6 max-w-xl font-mono text-xs uppercase leading-relaxed tracking-[0.18em] text-ink/55 sm:text-sm"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.0, ease: EASE }}
                    >
                        Lead generation · Technology · Automation · Marketing — one connected system, always on.
                    </motion.p>

                    <motion.div
                        className="mt-10 flex flex-wrap items-center gap-4"
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.15, ease: EASE }}
                    >
                        <button
                            data-testid="hero-cta-button"
                            onClick={onOpenModal}
                            className="btn-primary group flex items-center gap-2.5 border-2 border-ink bg-ink px-7 py-4 font-mono text-sm font-bold uppercase tracking-wider text-paper"
                        >
                            Build My Queue
                            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                        </button>
                        <button
                            data-testid="hero-secondary-cta"
                            onClick={() => scrollToSection("system")}
                            className="group flex items-center gap-2 border-b-2 border-inkblue/40 pb-1 font-mono text-sm font-bold uppercase tracking-wider text-inkblue transition-colors duration-300 hover:border-inkblue"
                        >
                            See the system
                            <ArrowDown size={15} className="transition-transform duration-300 group-hover:translate-y-1" />
                        </button>
                    </motion.div>
                </motion.div>

                <motion.div style={{ y: yScene, opacity: sceneOpacity }} className="mt-16">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 1.3, ease: EASE }}
                    >
                        <QueueScene />
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};
